import { onboardingSidebar } from '@/lib/constants';
import { removeTrailingSlashes } from '@/lib/utils';
import PropTypes from 'prop-types';
import { memo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExtraMenuItem, WizardMenuItem } from '.';

const {
  PERSONAL_INFO_STEP_ID,
  WORKDAY_TASK_STEP_ID,
} = onboardingSidebar;

const callbackCache = {};

const Menu = ({
  items,
  isPopup,
  currentStep,
  setCurrentStep,
  setNextStep,
}) => {
  const navigate = useNavigate();

  // I wished to use `useStableCallback()` instead.
  // However, the `react-hooks/rules-of-hooks` eslint rule throws an exception.
  //
  // It is unsafe to use any hooks (in particular `useRef`) conditionally.
  // See https://reactjs.org/docs/hooks-rules.html
  const onItemClick = (id, path) => {
    const cached = callbackCache[id] = callbackCache[id] ?? {
      ref: null,
      callback: () => cached.ref(),
    };

    cached.ref = () => {
      const applyTransition = () => {
        setNextStep(null);
        setCurrentStep(id);

        if (!isPopup) {
          navigate(`/onboarding/${path}`, { replace: true });
        }
      };

      const declineTransition = () => {
        setNextStep(null);
      };

      // When going out of the Wizard Forms, the intention to perform a transition is expressed.
      // Forms subscribed to such intention expressions and can take care of additional stuff
      // prior to applying or denying transition. For example, run validations/save processes.
      //
      // In all other cases, the location, i.e., `currentStep` and URL (if supported) change immediately.
      // TODO: currentStep === WORKDAY_TASK_STEP_ID (prev.: items.isWorkdayTasks(currentStep)) condition doesn't match the comment above
      //       maybe it's possible to use component "unmount" event to reload Rep Profile on WorkdayTasksTab component
      //       alternatively, all the forms will utilize the same logic of transition handling, i.e. listen for nextStep changes
      //       see 889bb0adb0d4e7f57dffa2dbcf2fde258a6f46fb commit for details
      if (items.isWizardItem(currentStep) || currentStep === WORKDAY_TASK_STEP_ID) {
        setNextStep({ applyTransition, declineTransition });
      } else {
        applyTransition();
      }
    };

    return cached.callback;
  };

  const location = useLocation();

  // This effect is aimed to fix the out-of-sync `currentStep/currentPath` issue.
  //
  // In Outlet mode (which is the opposite of Popup mode) on initial mount
  // the `currentStep` equals its default value set in the Redux store.
  //
  // However, this default value can differ from an actual `currentStep`.
  // The actual `currentStep` value can be resolved by a `currentPath`.
  useEffect(() => {
    if (!isPopup) {
      const landedStep = items.resolveItemByPath(
        removeTrailingSlashes(location.pathname).split('/').pop(),
      )?.id ?? PERSONAL_INFO_STEP_ID;

      if (landedStep !== currentStep) {
        setCurrentStep(landedStep);
      }
    }

    // This effect needs to be run only on the initial component mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <nav aria-label="Progress">
        <ol>
          {items.wizard.map(({
            id,
            path,
            label,
            shouldDisplay,
            completed,
          }) => shouldDisplay && (
            <WizardMenuItem
              key={id}
              label={label}
              onClick={onItemClick(id, path)}
              selected={id === currentStep}
              completed={completed}
            />
          ))}
        </ol>
      </nav>
      <nav aria-label="Extra">
        <ul>
          {items.extra.map(({
            id,
            path,
            label,
            shouldDisplay,
          }) => shouldDisplay && (
            <ExtraMenuItem
              key={id}
              label={label}
              onClick={onItemClick(id, path)}
              selected={id === currentStep}
            />
          ))}
        </ul>
      </nav>
    </>
  );
};

Menu.propTypes = {
  items: PropTypes.shape({
    wizard: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      shouldDisplay: PropTypes.bool.isRequired,
      completed: PropTypes.bool.isRequired,
    })).isRequired,
    extra: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      shouldDisplay: PropTypes.bool.isRequired,
    })).isRequired,
    isWizardItem: PropTypes.func.isRequired,
    resolveItemByPath: PropTypes.func.isRequired,
  }).isRequired,
  isPopup: PropTypes.bool.isRequired,
  currentStep: PropTypes.number.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  setNextStep: PropTypes.func.isRequired,
};

export default memo(Menu);
